<template>
  <div class="bg-gray-50 rounded-xl p-4">
    <h4 class="font-semibold text-gray-700 mb-3 flex items-center">
      <ArrowsPointingOutIcon class="w-4 h-4 mr-2" />
      Output Size
    </h4>
    
    <div class="space-y-3">
      <div class="space-y-2">
        <label class="text-xs text-gray-500">Preset Sizes</label>
        <div class="relative">
          <select 
            v-model="selectedPreset"
            @change="handlePresetChange"
            class="w-full px-3 py-2 pr-10 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent cursor-pointer appearance-none"
          >
            <option v-for="size in presetSizes" :key="size.name" :value="size">
              {{ size.name }}
            </option>
          </select>
          <div class="absolute inset-y-0 right-0 flex items-center px-3 pointer-events-none">
            <ChevronDownIcon class="w-4 h-4 text-gray-400" />
          </div>
        </div>
      </div>
      
      <div class="border-t border-gray-200 pt-3">
        <label class="flex items-center text-xs text-gray-500 mb-2">
          <input 
            type="checkbox" 
            v-model="useCustomSize"
            class="mr-2 rounded"
          >
          Custom Size
        </label>
        
        <div v-if="useCustomSize" class="grid grid-cols-2 gap-2">
          <div>
            <label class="text-xs text-gray-400">Width</label>
            <input 
              type="number" 
              v-model.number="customWidth"
              min="1"
              class="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
          </div>
          <div>
            <label class="text-xs text-gray-400">Height</label>
            <input 
              type="number" 
              v-model.number="customHeight"
              min="1"
              class="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
          </div>
        </div>
      </div>
      
      <div class="bg-white rounded-lg p-3 text-sm">
        <div class="flex justify-between text-gray-600">
          <span>Output:</span>
          <span class="font-semibold">
            {{ displaySize }}
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, watch, onMounted, computed } from 'vue'
import { ArrowsPointingOutIcon, ChevronDownIcon } from '@heroicons/vue/24/outline'

const props = defineProps({
  modelValue: {
    type: Object,
    required: true
  },
  presetSizes: {
    type: Array,
    required: true
  }
})

const emit = defineEmits(['update:modelValue'])

const selectedPreset = ref(props.presetSizes[1])
const useCustomSize = ref(false)
const customWidth = ref(800)
const customHeight = ref(600)

const displaySize = computed(() => {
  if (props.modelValue.width && props.modelValue.height) {
    return `${props.modelValue.width} × ${props.modelValue.height} px`
  }
  return 'Free Ratio'
})

const handlePresetChange = () => {
  const preset = selectedPreset.value
  
  console.log('handlePresetChange called with preset:', preset)
  
  if (preset.width === null) {
    emit('update:modelValue', {
      name: preset.name,
      width: null,
      height: null,
      ratio: null,
      unit: 'px'
    })
    useCustomSize.value = true
  } else {
    const newValue = {
      name: preset.name,
      width: preset.width,
      height: preset.height,
      ratio: preset.ratio,
      unit: 'px'
    }
    
    Object.assign(props.modelValue, newValue)
    emit('update:modelValue', props.modelValue)
    
    customWidth.value = preset.width
    customHeight.value = preset.height
    useCustomSize.value = false
  }
  
  console.log('After update - modelValue:', props.modelValue)
}

onMounted(() => {
  handlePresetChange()
})

watch([customWidth, customHeight], ([width, height]) => {
  if (useCustomSize.value) {
    const newValue = {
      name: 'Custom',
      width: width,
      height: height,
      ratio: width / height,
      unit: 'px'
    }
    Object.assign(props.modelValue, newValue)
    emit('update:modelValue', props.modelValue)
    console.log('Custom size updated:', newValue)
  }
})

watch(useCustomSize, (newValue) => {
  console.log('useCustomSize changed to:', newValue)
  if (newValue) {
    const customValue = {
      name: 'Custom',
      width: customWidth.value,
      height: customHeight.value,
      ratio: customWidth.value / customHeight.value,
      unit: 'px'
    }
    Object.assign(props.modelValue, customValue)
    emit('update:modelValue', props.modelValue)
    console.log('Switched to custom size:', customValue)
  }
})
</script>
